import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '@/lib/api/client';
import { APP_PAGES, type AppPage } from '@/lib/appRegistry';

// ── Feature inventory — the dev dash main table ─────────────────────────────
// One row per app surface: what it is, a live sandboxed demo of it, the queued
// change requests against it (attachments welcome for long specs/prototypes),
// and the changelog written when each request is fulfilled. The demo buttons
// open the page with ?demo=<role>: the whole dataset loads into that tab's
// RAM and no request touches the server — public, safe, resettable.

interface ChangeRequest {
  id: number;
  page: string;
  element?: string | null;
  title: string;
  details?: string | null;
  status: 'proposed' | 'open' | 'in_progress' | 'done' | 'rejected';
  source?: string;
  priority: 'low' | 'normal' | 'high';
  changelog?: string | null;
  attachment_path?: string | null;
  attachment_name?: string | null;
  created_at: string;
  fulfilled_at?: string | null;
}

const STATUS_CLS: Record<string, string> = {
  open: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  rejected: 'bg-gray-100 text-gray-500',
};

const demoRoleFor = (p: AppPage): string =>
  p.route.startsWith('/portal/client') ? 'client' : p.route.startsWith('/portal') ? 'employee' : 'admin';

const shotSlug = (route: string) => route.replace(/^\//, '').replace(/\//g, '-') || 'root';

const AREAS = ['Admin', 'Portal', 'Public', 'Backend'] as const;
export const NEW_FEATURE_PAGE = '🆕 New feature';
export const GENERAL_PAGE = 'General';

export function FeatureInventoryTable() {
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [areas, setAreas] = useState<Set<string>>(() => new Set(AREAS));
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addingFor, setAddingFor] = useState<string | null>(null); // `${page}|${element}`
  const [fTitle, setFTitle] = useState('');
  const [fDetails, setFDetails] = useState('');
  const [fElement, setFElement] = useState('');
  const [fPriority, setFPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [fFile, setFFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState<string | null>(null);
  const [preview, setPreview] = useState<AppPage | null>(null);
  const [shotsBusy, setShotsBusy] = useState(false);
  const [newFeatureOpen, setNewFeatureOpen] = useState<false | 'feature' | 'general'>(false);
  const [queuePaused, setQueuePaused] = useState(false);
  // "Fulfill now?" prompt after any submission — holds the new request's id
  const [askNow, setAskNow] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () =>
    apiClient.get('/dev/change-requests').then(r => setRequests(r.data.data ?? [])).catch(() => {});
  useEffect(() => {
    load();
    apiClient.get('/dev/queue-state').then(r => setQueuePaused(!!r.data.data?.paused)).catch(() => {});
  }, []);

  const togglePause = async () => {
    const next = !queuePaused;
    setQueuePaused(next);
    try { await apiClient.put('/dev/queue-state', { paused: next }); }
    catch { setQueuePaused(!next); }
  };

  const [startMsg, setStartMsg] = useState('');
  const startNow = async () => {
    try {
      await apiClient.put('/dev/queue-state', { run_now: true });
      setQueuePaused(false);
      setStartMsg('Start signal sent — Claude begins within a few minutes');
      setTimeout(() => setStartMsg(''), 6000);
    } catch { setStartMsg('Could not send the start signal'); }
  };

  const markFulfillNow = async (id: number, now: boolean) => {
    setAskNow(null);
    if (!now) return;
    try {
      await apiClient.put(`/dev/change-requests/${id}`, { fulfill_now: true, priority: 'high' });
      load();
    } catch { /* stays a normal queue item */ }
  };

  const byPage = useMemo(() => {
    const m = new Map<string, ChangeRequest[]>();
    for (const r of requests) (m.get(r.page) || m.set(r.page, []).get(r.page)!).push(r);
    return m;
  }, [requests]);

  const q = search.trim().toLowerCase();
  const rows = APP_PAGES.filter(p => {
    if (!areas.has(p.area)) return false;
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      (p.desc || '').toLowerCase().includes(q) ||
      p.route.toLowerCase().includes(q) ||
      (p.elements || []).some(el => el.name.toLowerCase().includes(q))
    );
  });

  const toggleArea = (a: string) => setAreas(prev => {
    const next = new Set(prev);
    if (next.has(a)) next.delete(a); else next.add(a);
    return next.size ? next : prev; // never allow zero areas
  });
  const toggleExpand = (name: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const submit = async (page: AppPage, lockedElement?: string) => {
    if (!fTitle.trim()) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('page', page.name);
      const el = lockedElement ?? fElement;
      if (el) form.append('element', el);
      form.append('title', fTitle.trim());
      if (fDetails.trim()) form.append('details', fDetails.trim());
      form.append('priority', fPriority);
      if (fFile) form.append('attachment', fFile);
      const r = await apiClient.post('/dev/change-requests', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFTitle(''); setFDetails(''); setFElement(''); setFPriority('normal'); setFFile(null);
      setAddingFor(null);
      load();
      if (r.data?.data?.id) setAskNow(r.data.data.id);
    } finally { setBusy(false); }
  };

  const withdraw = async (r: ChangeRequest) => {
    setRequests(rs => rs.map(x => x.id === r.id ? { ...x, status: 'rejected' as const } : x));
    try { await apiClient.put(`/dev/change-requests/${r.id}`, { status: 'rejected' }); } catch { load(); }
  };

  const totalOpen = requests.filter(r => r.status === 'open' || r.status === 'in_progress').length;

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <span className="material-symbols-outlined text-[#C77D3F]">table_view</span>
        <h2 className="font-bold text-gray-900">Feature inventory</h2>
        {totalOpen > 0 && (
          <span className="text-[11px] font-bold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">{totalOpen} requests in queue</span>
        )}
        <button onClick={startNow}
          title="Override the schedule — Claude starts working the open queue within a few minutes"
          className="h-7 px-2.5 rounded-full text-xs font-bold bg-green-600 text-white flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">bolt</span> Start now
        </button>
        {startMsg && <span className="text-[11px] font-semibold text-green-700">{startMsg}</span>}
        <button onClick={togglePause}
          title={queuePaused ? 'Queue fulfilment is PAUSED — Claude will not work requests until you continue' : 'Claude works the queue on its routine checks — click to pause'}
          className={`h-7 px-2.5 rounded-full text-xs font-bold flex items-center gap-1 border ${queuePaused ? 'bg-red-600 text-white border-red-600' : 'bg-white text-green-700 border-green-300'}`}>
          <span className="material-symbols-outlined text-sm">{queuePaused ? 'pause_circle' : 'play_circle'}</span>
          {queuePaused ? 'Queue paused' : 'Queue active'}
        </button>
        <button onClick={() => setNewFeatureOpen('general')}
          className="h-7 px-2.5 rounded-full text-xs font-bold bg-[#2C1810] text-white flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">edit_note</span> General request
        </button>
        <button onClick={() => setNewFeatureOpen('feature')}
          className="h-7 px-2.5 rounded-full text-xs font-bold bg-[#C77D3F] text-white flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">add</span> New feature
        </button>
        <button
          onClick={async () => { setShotsBusy(true); try { await apiClient.post('/dev/screenshots/generate', {}); } finally { setTimeout(() => setShotsBusy(false), 4000); } }}
          disabled={shotsBusy}
          title="Re-capture page screenshots (takes ~1 min, refresh after)"
          className="h-7 px-2.5 rounded-full text-xs font-semibold border border-gray-200 text-gray-500 disabled:opacity-40 flex items-center gap-1">
          <span className={`material-symbols-outlined text-sm ${shotsBusy ? 'animate-spin' : ''}`}>photo_camera</span>
          {shotsBusy ? 'Capturing…' : 'Refresh shots'}
        </button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2 top-1/2 -translate-y-1/2 text-base text-gray-300">search</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setSearch(''); }}
              placeholder="Search features, elements…"
              className="h-8 w-56 pl-7 pr-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
            />
          </div>
          {AREAS.map(a => (
            <label key={a} className="flex items-center gap-1 text-xs font-semibold text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={areas.has(a)} onChange={() => toggleArea(a)} className="accent-[#C77D3F]" />
              {a}
            </label>
          ))}
        </div>
      </div>

      {/* Claude's suggestions — routine project audits propose improvements;
          approving one queues it like any change request */}
      {requests.some(r => r.status === 'proposed') && (
        <div className="border-b border-gray-200 bg-[#FAF6F0] px-3 py-2.5">
          <p className="text-xs font-bold text-[#7a4a22] flex items-center gap-1.5 mb-1.5">
            <span className="material-symbols-outlined text-base text-[#C77D3F]">tips_and_updates</span>
            Claude's suggestions — approve to queue, dismiss to skip
            <span className="text-[10px] font-bold bg-[#C77D3F] text-white rounded-full px-1.5 py-0.5">
              {requests.filter(r => r.status === 'proposed').length}
            </span>
          </p>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-1.5">
            {requests.filter(r => r.status === 'proposed').map(r => (
              <div key={r.id} className="bg-white border border-[#C77D3F]/20 rounded-lg px-2.5 py-2">
                <p className="text-xs font-semibold text-gray-900 leading-snug">{r.title}</p>
                <p className="text-[10px] text-gray-400">{r.page}{r.element ? ` › ${r.element}` : ''} · {r.priority}</p>
                {r.details && <p className="text-[11px] text-gray-600 mt-1 leading-snug line-clamp-3">{r.details}</p>}
                <div className="flex gap-1.5 mt-1.5">
                  <button
                    onClick={async () => {
                      setRequests(rs => rs.map(x => x.id === r.id ? { ...x, status: 'open' as const } : x));
                      try { await apiClient.put(`/dev/change-requests/${r.id}`, { status: 'open' }); } catch { load(); }
                    }}
                    className="h-7 px-2.5 rounded-md bg-green-600 text-white text-[11px] font-bold">Approve</button>
                  <button
                    onClick={async () => {
                      setRequests(rs => rs.map(x => x.id === r.id ? { ...x, status: 'rejected' as const } : x));
                      try { await apiClient.put(`/dev/change-requests/${r.id}`, { status: 'rejected' }); } catch { load(); }
                    }}
                    className="h-7 px-2.5 rounded-md border border-gray-200 text-[11px] font-semibold text-gray-500">Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[72rem]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
              <th className="px-3 py-2 font-semibold w-64">Feature</th>
              <th className="px-2 py-2 font-semibold">Area</th>
              <th className="px-2 py-2 font-semibold">Demo preview</th>
              <th className="px-2 py-2 font-semibold w-80">Change requests</th>
              <th className="px-2 py-2 font-semibold w-72">Changelog</th>
              <th className="px-2 py-2 font-semibold">Queue</th>
              <th className="px-2 py-2 font-semibold whitespace-nowrap">Last fulfilled</th>
              <th className="px-2 py-2 font-semibold">Elements</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 align-top">
            {/* Requests for pages/elements that don't exist yet */}
            {requests.some(r => (r.page === NEW_FEATURE_PAGE || r.page === GENERAL_PAGE) && r.status !== 'rejected') && (
              <tr className="bg-[#C77D3F]/5">
                <td className="px-3 py-2.5">
                  <p className="font-semibold text-[#8a5526] leading-snug flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">auto_awesome</span> New features & general requests
                  </p>
                  <p className="text-xs text-gray-500">Not tied to an existing page — new builds and anything-anywhere changes</p>
                </td>
                <td className="px-2 py-2.5" /><td className="px-2 py-2.5" />
                <td className="px-2 py-2.5">
                  <div className="space-y-1">
                    {requests.filter(r => (r.page === NEW_FEATURE_PAGE || r.page === GENERAL_PAGE) && (r.status === 'open' || r.status === 'in_progress')).map(r => (
                      <div key={r.id} className="flex items-start gap-1.5 bg-white border border-[#C77D3F]/20 rounded-lg px-2 py-1">
                        <span className={`mt-0.5 text-[9px] font-bold px-1 rounded ${STATUS_CLS[r.status]}`}>{r.status === 'in_progress' ? 'WIP' : 'open'}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-800">{r.title}</p>
                          <p className="text-[9px] text-gray-400">{r.element || ''} · {r.priority}
                            {r.attachment_path && <a href={`/${r.attachment_path}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#C77D3F] font-semibold">📎</a>}
                          </p>
                        </div>
                        <button onClick={() => withdraw(r)} className="text-gray-300 hover:text-red-500"><span className="material-symbols-outlined text-sm">close</span></button>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-2.5">
                  <div className="space-y-1">
                    {requests.filter(r => (r.page === NEW_FEATURE_PAGE || r.page === GENERAL_PAGE) && r.status === 'done').map(r => (
                      <details key={r.id} className="bg-green-50/60 border border-green-100 rounded-lg px-2 py-1">
                        <summary className="text-xs text-gray-700 cursor-pointer"><span className="text-green-700 font-semibold">✓</span> {r.title}</summary>
                        {r.changelog && <p className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap">{r.changelog}</p>}
                      </details>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-2.5" /><td className="px-2 py-2.5" /><td className="px-2 py-2.5" />
              </tr>
            )}
            {rows.map(p => {
              const reqs = byPage.get(p.name) || [];
              const isOpen = expanded.has(p.name) || (!!q && (p.elements || []).some(el => el.name.toLowerCase().includes(q)));
              // Collapsed: the row shows everything for the page. Expanded: the
              // parent keeps whole-page requests; element rows carry their own.
              const scoped = (list: ChangeRequest[], el?: string) =>
                isOpen ? list.filter(r => (r.element || '') === (el || '')) : (el ? [] : list);
              const active = scoped(reqs.filter(r => r.status === 'open' || r.status === 'in_progress'));
              const done = scoped(reqs.filter(r => r.status === 'done'));
              const allActive = reqs.filter(r => r.status === 'open' || r.status === 'in_progress');
              const lastDone = reqs.filter(r => r.status === 'done').map(r => r.fulfilled_at || '').sort().pop();
              const topPrio = allActive.some(r => r.priority === 'high') ? 'high' : allActive.some(r => r.priority === 'normal') ? 'normal' : allActive.length ? 'low' : null;
              const adding = addingFor === `${p.name}|`;
              const hasKids = (p.elements?.length || 0) > 0;
              return (
                <Fragment key={`${p.area}-${p.name}`}>
                <tr className="hover:bg-gray-50/50">
                  {/* 1 · Feature: expand chevron + title / description / route */}
                  <td className="px-3 py-2.5">
                    <div className="flex items-start gap-1">
                      {hasKids ? (
                        <button onClick={() => toggleExpand(p.name)} aria-label={isOpen ? 'Collapse' : 'Expand'}
                          className="mt-0.5 text-gray-400 hover:text-gray-700 flex-shrink-0">
                          <span className={`material-symbols-outlined text-lg transition-transform ${isOpen ? 'rotate-90' : ''}`}>chevron_right</span>
                        </button>
                      ) : <span className="w-5 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 leading-snug">{p.name}</p>
                        {p.desc && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{p.desc}</p>}
                        {p.route && <code className="text-[10px] text-gray-400">{p.route}</code>}
                      </div>
                    </div>
                  </td>
                  {/* Area */}
                  <td className="px-2 py-2.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${p.area === 'Admin' ? 'bg-[#C77D3F]/10 text-[#8a5526]' : p.area === 'Portal' ? 'bg-blue-50 text-blue-700' : p.area === 'Backend' ? 'bg-gray-800 text-gray-100' : 'bg-green-50 text-green-700'}`}>{p.area}</span>
                  </td>
                  {/* 2 · Demo preview — screenshot thumb; click = live sandboxed overlay */}
                  <td className="px-2 py-2.5">
                    {!p.route ? <span className="text-[10px] text-gray-300">no UI</span> : (
                    <button onClick={() => setPreview(p)} className="block group/shot text-left" title="Open interactive demo preview">
                      <span className="block w-44 h-[6.9rem] rounded-lg border border-gray-200 overflow-hidden bg-gray-100 relative">
                        <img
                          src={`/uploads/screenshots/${shotSlug(p.route)}.png`}
                          alt={`${p.name} screenshot`}
                          loading="lazy"
                          className="w-full h-full object-cover object-top"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/shot:bg-black/25 transition-colors">
                          <span className="material-symbols-outlined text-3xl text-white opacity-0 group-hover/shot:opacity-100 drop-shadow">play_circle</span>
                        </span>
                      </span>
                      <span className="block text-[9px] text-gray-400 mt-0.5">tap to preview · sandboxed, no server calls</span>
                    </button>
                    )}
                  </td>
                  {/* 3 · Change requests + add (with attachment) */}
                  <td className="px-2 py-2.5">
                    <div className="space-y-1">
                      {active.map(r => (
                        <div key={r.id} className="flex items-start gap-1.5 bg-amber-50/60 border border-amber-100 rounded-lg px-2 py-1">
                          <span className={`mt-0.5 text-[9px] font-bold px-1 rounded ${STATUS_CLS[r.status]}`}>{r.status === 'in_progress' ? 'WIP' : 'open'}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-800 leading-snug">{r.title}</p>
                            <p className="text-[9px] text-gray-400">
                              {r.element ? `${r.element} · ` : ''}{r.priority}
                              {r.attachment_path && (
                                <a href={`/${r.attachment_path}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#C77D3F] font-semibold">
                                  📎 {r.attachment_name || 'attachment'}
                                </a>
                              )}
                            </p>
                          </div>
                          <button onClick={() => withdraw(r)} title="Withdraw" className="text-gray-300 hover:text-red-500">
                            <span className="material-symbols-outlined text-sm">close</span>
                          </button>
                        </div>
                      ))}
                      {adding ? (
                        <div className="border border-[#C77D3F]/40 rounded-lg p-2 space-y-1.5 bg-white">
                          <input autoFocus value={fTitle} onChange={e => setFTitle(e.target.value)} placeholder="What should change?"
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none" />
                          <textarea value={fDetails} onChange={e => setFDetails(e.target.value)} rows={2}
                            placeholder="Details / acceptance criteria (optional — or attach a doc)"
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs resize-none focus:outline-none" />
                          <div className="flex items-center gap-1 flex-wrap">
                            <select value={fElement} onChange={e => setFElement(e.target.value)}
                              className="border border-gray-200 rounded-md px-1.5 py-1 text-[10px] bg-white focus:outline-none max-w-[9rem]">
                              <option value="">Whole page</option>
                              {(p.elements || []).map(el => <option key={el.name} value={el.name}>{el.name}</option>)}
                            </select>
                            <select value={fPriority} onChange={e => setFPriority(e.target.value as 'low' | 'normal' | 'high')}
                              className="border border-gray-200 rounded-md px-1.5 py-1 text-[10px] bg-white focus:outline-none">
                              <option value="low">low</option><option value="normal">normal</option><option value="high">high</option>
                            </select>
                            <button onClick={() => fileRef.current?.click()}
                              className={`h-6 px-1.5 rounded-md border text-[10px] font-semibold ${fFile ? 'border-[#C77D3F] text-[#C77D3F]' : 'border-gray-200 text-gray-400'}`}>
                              📎 {fFile ? fFile.name.slice(0, 14) : 'Attach'}
                            </button>
                            <input ref={fileRef} type="file" className="sr-only"
                              onChange={e => setFFile(e.target.files?.[0] || null)} />
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => submit(p)} disabled={busy || !fTitle.trim()}
                              className="flex-1 h-7 rounded-md bg-[#C77D3F] text-white text-[11px] font-bold disabled:opacity-40">Queue it</button>
                            <button onClick={() => setAddingFor(null)} className="h-7 px-2 rounded-md border border-gray-200 text-[11px] text-gray-500">✕</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setAddingFor(`${p.name}|`); setFTitle(''); setFDetails(''); setFElement(''); setFFile(null); }}
                          className="h-6 px-2 rounded-md border border-dashed border-gray-300 text-[10px] font-semibold text-gray-400 hover:text-[#C77D3F] hover:border-[#C77D3F]/50">
                          + request
                        </button>
                      )}
                    </div>
                  </td>
                  {/* 4 · Changelog */}
                  <td className="px-2 py-2.5">
                    {done.length === 0 ? <span className="text-[10px] text-gray-300">—</span> : (
                      <div className="space-y-1">
                        {done.slice(0, logOpen === p.name ? undefined : 2).map(r => (
                          <details key={r.id} className="bg-green-50/60 border border-green-100 rounded-lg px-2 py-1">
                            <summary className="text-xs text-gray-700 cursor-pointer leading-snug">
                              <span className="text-green-700 font-semibold">✓</span> {r.title}
                              <span className="text-[9px] text-gray-400 ml-1">
                                {r.fulfilled_at ? new Date(r.fulfilled_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : ''}
                              </span>
                            </summary>
                            {r.changelog && <p className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap">{r.changelog}</p>}
                          </details>
                        ))}
                        {done.length > 2 && (
                          <button onClick={() => setLogOpen(o => o === p.name ? null : p.name)}
                            className="text-[10px] font-semibold text-green-700">
                            {logOpen === p.name ? 'show less' : `+${done.length - 2} more`}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Queue status (page + all elements) */}
                  <td className="px-2 py-2.5 whitespace-nowrap">
                    {allActive.length === 0
                      ? <span className="text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-1.5 py-0.5">clear</span>
                      : <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${topPrio === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                          {allActive.length} queued{topPrio === 'high' ? ' · high' : ''}
                        </span>}
                  </td>
                  {/* Last fulfilled */}
                  <td className="px-2 py-2.5 text-[11px] text-gray-500 whitespace-nowrap">
                    {lastDone ? new Date(lastDone).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  {/* Elements */}
                  <td className="px-2 py-2.5">
                    {hasKids && (
                      <button onClick={() => toggleExpand(p.name)} className="text-[10px] font-semibold text-gray-500 hover:text-[#C77D3F]">
                        {p.elements!.length} {isOpen ? '▾' : '▸'}
                      </button>
                    )}
                  </td>
                </tr>

                {/* Element sub-rows — each takes its own change requests */}
                {isOpen && (p.elements || []).map(el => {
                  const elActive = reqs.filter(r => (r.status === 'open' || r.status === 'in_progress') && r.element === el.name);
                  const elDone = reqs.filter(r => r.status === 'done' && r.element === el.name);
                  const elKey = `${p.name}|${el.name}`;
                  const elAdding = addingFor === elKey;
                  const elLast = elDone.map(r => r.fulfilled_at || '').sort().pop();
                  return (
                    <tr key={elKey} className="bg-gray-50/40">
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5 pl-6">
                          <span className="material-symbols-outlined text-sm text-gray-300">subdirectory_arrow_right</span>
                          <span className="text-xs text-gray-700">{el.name}</span>
                          <span className="text-[9px] font-semibold text-gray-400 bg-gray-100 rounded px-1">{el.kind}</span>
                        </div>
                        {el.desc && <p className="text-[10px] text-gray-400 pl-11">{el.desc}</p>}
                      </td>
                      <td className="px-2 py-1.5" />
                      <td className="px-2 py-1.5" />
                      <td className="px-2 py-1.5">
                        <div className="space-y-1">
                          {elActive.map(r => (
                            <div key={r.id} className="flex items-start gap-1.5 bg-amber-50/60 border border-amber-100 rounded-lg px-2 py-1">
                              <span className={`mt-0.5 text-[9px] font-bold px-1 rounded ${STATUS_CLS[r.status]}`}>{r.status === 'in_progress' ? 'WIP' : 'open'}</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-800 leading-snug">{r.title}</p>
                                <p className="text-[9px] text-gray-400">
                                  {r.priority}
                                  {r.attachment_path && (
                                    <a href={`/${r.attachment_path}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#C77D3F] font-semibold">📎 {r.attachment_name || 'attachment'}</a>
                                  )}
                                </p>
                              </div>
                              <button onClick={() => withdraw(r)} title="Withdraw" className="text-gray-300 hover:text-red-500">
                                <span className="material-symbols-outlined text-sm">close</span>
                              </button>
                            </div>
                          ))}
                          {elAdding ? (
                            <div className="border border-[#C77D3F]/40 rounded-lg p-2 space-y-1.5 bg-white">
                              <input autoFocus value={fTitle} onChange={e => setFTitle(e.target.value)} placeholder={`Change for “${el.name}”…`}
                                className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none" />
                              <textarea value={fDetails} onChange={e => setFDetails(e.target.value)} rows={2}
                                placeholder="Details (optional — or attach a doc)"
                                className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs resize-none focus:outline-none" />
                              <div className="flex items-center gap-1 flex-wrap">
                                <select value={fPriority} onChange={e => setFPriority(e.target.value as 'low' | 'normal' | 'high')}
                                  className="border border-gray-200 rounded-md px-1.5 py-1 text-[10px] bg-white focus:outline-none">
                                  <option value="low">low</option><option value="normal">normal</option><option value="high">high</option>
                                </select>
                                <button onClick={() => fileRef.current?.click()}
                                  className={`h-6 px-1.5 rounded-md border text-[10px] font-semibold ${fFile ? 'border-[#C77D3F] text-[#C77D3F]' : 'border-gray-200 text-gray-400'}`}>
                                  📎 {fFile ? fFile.name.slice(0, 14) : 'Attach'}
                                </button>
                                <button onClick={() => submit(p, el.name)} disabled={busy || !fTitle.trim()}
                                  className="ml-auto h-6 px-2.5 rounded-md bg-[#C77D3F] text-white text-[10px] font-bold disabled:opacity-40">Queue it</button>
                                <button onClick={() => setAddingFor(null)} className="h-6 px-1.5 rounded-md border border-gray-200 text-[10px] text-gray-500">✕</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => { setAddingFor(elKey); setFTitle(''); setFDetails(''); setFFile(null); }}
                              className="h-5 px-1.5 rounded border border-dashed border-gray-300 text-[9px] font-semibold text-gray-400 hover:text-[#C77D3F] hover:border-[#C77D3F]/50">
                              + request
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {elDone.length > 0 && (
                          <div className="space-y-1">
                            {elDone.map(r => (
                              <details key={r.id} className="bg-green-50/60 border border-green-100 rounded-lg px-2 py-1">
                                <summary className="text-[11px] text-gray-700 cursor-pointer"><span className="text-green-700 font-semibold">✓</span> {r.title}</summary>
                                {r.changelog && <p className="text-[10px] text-gray-600 mt-1 whitespace-pre-wrap">{r.changelog}</p>}
                              </details>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {elActive.length > 0 && (
                          <span className="text-[9px] font-bold text-amber-800 bg-amber-100 rounded-full px-1.5 py-0.5">{elActive.length}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-gray-400 whitespace-nowrap">
                        {elLast ? new Date(elLast).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : ''}
                      </td>
                      <td className="px-2 py-1.5" />
                    </tr>
                  );
                })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {newFeatureOpen && (
        <NewFeatureModal mode={newFeatureOpen} onClose={() => setNewFeatureOpen(false)}
          onChanged={(id?: number) => { load(); if (id) setAskNow(id); }} />
      )}
      {askNow !== null && (
        <FulfillNowDialog onAnswer={now => markFulfillNow(askNow, now)} />
      )}
      {preview && (
        <PreviewOverlay
          page={preview}
          requests={requests}
          onClose={() => setPreview(null)}
          onChanged={(id?: number) => { load(); if (id) setAskNow(id); }}
        />
      )}
    </div>
  );
}

// ── "Fulfill now?" — asked after every submission ───────────────────────────
function FulfillNowDialog({ onAnswer }: { onAnswer: (now: boolean) => void }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-5 space-y-3 text-center">
        <span className="material-symbols-outlined text-3xl text-[#C77D3F]">bolt</span>
        <p className="font-bold text-gray-900 text-sm">Queued ✓ — fulfill now?</p>
        <p className="text-xs text-gray-500">Yes marks it high-priority and Claude's short-interval watcher picks it up within ~15 minutes; No leaves it for the daily run.</p>
        <div className="flex gap-2">
          <button onClick={() => onAnswer(false)} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">No, daily run</button>
          <button onClick={() => onAnswer(true)} className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold">Yes, now</button>
        </div>
      </div>
    </div>
  );
}

// ── Preview overlay: live demo iframe on top, requests + changelog below ────
function PreviewOverlay({ page, requests, onClose, onChanged }: {
  page: AppPage;
  requests: ChangeRequest[];
  onClose: () => void;
  onChanged: (id?: number) => void;
}) {
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [element, setElement] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // View the page as any user — demo identities are the safe default; "My
  // session" reuses this browser's real login inside the frame.
  const [viewAs, setViewAs] = useState<string>(() => `demo-${demoRoleFor(page)}`);
  const fileRef = useRef<HTMLInputElement>(null);
  const frameSrc = viewAs === 'live' ? page.route : `${page.route}?demo=${viewAs.replace('demo-', '')}`;

  const mine = requests.filter(r => r.page === page.name);
  const active = mine.filter(r => r.status === 'open' || r.status === 'in_progress');
  const done = mine.filter(r => r.status === 'done');

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('page', page.name);
      if (element) form.append('element', element);
      form.append('title', title.trim());
      if (details.trim()) form.append('details', details.trim());
      form.append('priority', priority);
      if (file) form.append('attachment', file);
      const r = await apiClient.post('/dev/change-requests', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTitle(''); setDetails(''); setElement(''); setPriority('normal'); setFile(null);
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      onChanged(r.data?.data?.id);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
          <span className="material-symbols-outlined text-[#C77D3F]">play_circle</span>
          <div className="min-w-0">
            <p className="font-bold text-sm text-gray-900 truncate">{page.name}</p>
            <p className="text-[10px] text-gray-400">{page.route} · demo modes are sandboxed (no server calls); "My session" is your real login</p>
          </div>
          <div className="ml-auto flex items-center gap-1">
            {[
              { v: 'demo-admin', l: 'Demo admin' },
              { v: 'demo-employee', l: 'Demo worker' },
              { v: 'demo-client', l: 'Demo client' },
              { v: 'live', l: 'My session' },
            ].map(o => (
              <button key={o.v} onClick={() => setViewAs(o.v)}
                className={`h-7 px-2 rounded-lg text-[10px] font-bold border ${viewAs === o.v ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-500 border-gray-200'}`}>
                {o.l}
              </button>
            ))}
          </div>
          <a href={frameSrc} target="_blank" rel="noopener noreferrer"
            className="h-8 px-2.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 flex items-center gap-1">
            <span className="material-symbols-outlined text-base">open_in_new</span> Full tab
          </a>
          <button onClick={onClose} className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 flex items-center justify-center">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Interactive demo iframe */}
        <iframe
          key={viewAs}
          src={frameSrc}
          title={`${page.name} preview`}
          className="w-full h-[52vh] border-b border-gray-200 flex-shrink-0 bg-[#FAF6F0]"
        />

        {/* Requests + changelog */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 space-y-1.5">
            <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base text-[#C77D3F]">edit_note</span>
              Request a change to this page
              {sent && <span className="text-green-700 font-semibold ml-1">✓ queued</span>}
            </p>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What should change?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={2}
              placeholder="Details / acceptance criteria (optional — or attach a doc/prototype)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
            <div className="flex items-center gap-1.5 flex-wrap">
              <select value={element} onChange={e => setElement(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
                <option value="">Whole page</option>
                {(page.elements || []).map(el => <option key={el.name} value={el.name}>{el.name}</option>)}
              </select>
              {(['low', 'normal', 'high'] as const).map(pr => (
                <button key={pr} onClick={() => setPriority(pr)}
                  className={`h-8 px-2.5 rounded-lg text-xs font-semibold border capitalize ${priority === pr ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {pr}
                </button>
              ))}
              <button onClick={() => fileRef.current?.click()}
                className={`h-8 px-2.5 rounded-lg border text-xs font-semibold ${file ? 'border-[#C77D3F] text-[#C77D3F]' : 'border-gray-200 text-gray-400'}`}>
                📎 {file ? file.name.slice(0, 18) : 'Attach doc'}
              </button>
              <input ref={fileRef} type="file" className="sr-only" onChange={e => setFile(e.target.files?.[0] || null)} />
              <button onClick={submit} disabled={busy || !title.trim()}
                className="ml-auto h-8 px-4 rounded-lg bg-[#C77D3F] text-white text-xs font-bold disabled:opacity-40">
                Queue it
              </button>
            </div>
          </div>

          {active.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">In queue</p>
              <div className="space-y-1">
                {active.map(r => (
                  <div key={r.id} className="flex items-start gap-1.5 bg-amber-50/60 border border-amber-100 rounded-lg px-2.5 py-1.5">
                    <span className={`mt-0.5 text-[9px] font-bold px-1 rounded ${STATUS_CLS[r.status]}`}>{r.status === 'in_progress' ? 'WIP' : 'open'}</span>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-800">{r.title}</p>
                      <p className="text-[9px] text-gray-400">
                        {r.element ? `${r.element} · ` : ''}{r.priority}
                        {r.attachment_path && (
                          <a href={`/${r.attachment_path}`} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#C77D3F] font-semibold">📎 {r.attachment_name || 'attachment'}</a>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Changelog</p>
            {done.length === 0 ? (
              <p className="text-xs text-gray-300">Nothing fulfilled for this page yet.</p>
            ) : (
              <div className="space-y-1">
                {done.map(r => (
                  <details key={r.id} className="bg-green-50/60 border border-green-100 rounded-lg px-2.5 py-1.5">
                    <summary className="text-xs text-gray-700 cursor-pointer">
                      <span className="text-green-700 font-semibold">✓</span> {r.title}
                      <span className="text-[9px] text-gray-400 ml-1">
                        {r.fulfilled_at ? new Date(r.fulfilled_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </span>
                    </summary>
                    {r.changelog && <p className="text-[11px] text-gray-600 mt-1 whitespace-pre-wrap">{r.changelog}</p>}
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── New feature request — for pages/elements that don't exist yet ───────────
function NewFeatureModal({ mode, onClose, onChanged }: { mode: 'feature' | 'general'; onClose: () => void; onChanged: (id?: number) => void }) {
  const [name, setName] = useState('');
  const [area, setArea] = useState<string>('Admin');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('page', mode === 'general' ? GENERAL_PAGE : NEW_FEATURE_PAGE);
      if (mode === 'feature') form.append('element', area);
      form.append('title', name.trim());
      if (details.trim()) form.append('details', details.trim());
      form.append('priority', priority);
      if (file) form.append('attachment', file);
      const r = await apiClient.post('/dev/change-requests', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChanged(r.data?.data?.id);
      onClose();
    } finally { setBusy(false); }
  };

  const field = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
          <span className="material-symbols-outlined text-[#C77D3F]">{mode === 'general' ? 'edit_note' : 'auto_awesome'}</span>
          {mode === 'general' ? 'General update / change request' : 'Request a new feature / page / element'}
        </h3>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          placeholder={mode === 'general' ? 'What should change? (anything, anywhere)' : "Name it — e.g. 'Equipment tracking page'"} className={field} />
        <textarea value={details} onChange={e => setDetails(e.target.value)} rows={4} className={`${field} resize-none`}
          placeholder="Details / acceptance criteria. Attach a doc or prototype for anything long." />
        <div className="flex items-center gap-1.5 flex-wrap">
          {mode === 'feature' && (
            <select value={area} onChange={e => setArea(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white">
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          {(['low', 'normal', 'high'] as const).map(pr => (
            <button key={pr} onClick={() => setPriority(pr)}
              className={`h-8 px-2.5 rounded-lg text-xs font-semibold border capitalize ${priority === pr ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-500 border-gray-200'}`}>
              {pr}
            </button>
          ))}
          <button onClick={() => fileRef.current?.click()}
            className={`h-8 px-2.5 rounded-lg border text-xs font-semibold ${file ? 'border-[#C77D3F] text-[#C77D3F]' : 'border-gray-200 text-gray-400'}`}>
            📎 {file ? file.name.slice(0, 16) : 'Attach doc'}
          </button>
          <input ref={fileRef} type="file" className="sr-only" onChange={e => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
          <button onClick={submit} disabled={busy || !name.trim()}
            className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
            {busy ? 'Queuing…' : 'Queue it'}
          </button>
        </div>
      </div>
    </div>
  );
}
